import {
  Organisation,
  OrgSiteListEntry,
} from "@inrange/building-manager-api-client/models-organisation";
import { getFinancialPerformance } from "@inrange/building-manager-api-client/models-site";
import {
  isSiteOwnerOccupied,
  NoPaddedRow,
  TableListSortFilters,
} from "@inrange/theme-components";
import {
  OperationalLandlordSiteListHeader,
  OperationalLandlordSiteRow,
  Table,
} from "@inrange/theme-components/table";
import React, { useContext, useState } from "react";
import { UserContext } from "../auth/UserContext";
import { isDemoModeEnabled } from "../routes/SiteViewTabs/demoMode";
import {
  sortAscendingForColumn,
  sortByColumn,
  togglesortAscending,
} from "./SiteListSortMethods";
import { getLatestActualsMonthIndex } from "./sitelist-utils";

interface OperationalLandlordSiteListViewProps {
  operationalSites: OrgSiteListEntry[];
  organisation: Organisation;
  startingSortColumn: string;
}

const OperationalLandlordSiteListView: React.FC<
  OperationalLandlordSiteListViewProps
> = ({ operationalSites, organisation, startingSortColumn }) => {
  const [textFilter, setTextFilter] = useState("");
  const [sortColumn, setSortColumn] = useState(startingSortColumn);
  const [sortAscending, setSortAscending] = useState(
    sortAscendingForColumn(startingSortColumn)
  );
  sortByColumn(operationalSites, sortColumn, sortAscending, organisation);
  const { user } = useContext(UserContext);

  const handleColumnClick = (columnKey: string) => {
    if (sortColumn === columnKey) {
      togglesortAscending(setSortAscending);
      return;
    }
    setSortColumn(columnKey);
    setSortAscending(sortAscendingForColumn(columnKey));
  };

  const showHealthIndicator = isDemoModeEnabled(organisation.demo, user);

  const tableHead = (
    <OperationalLandlordSiteListHeader
      sortColumn={sortColumn}
      sortAscending={sortAscending}
      handleColumnClick={handleColumnClick}
      showHealthIndicator={showHealthIndicator}
    />
  );

  const tableRows = operationalSites.map((site) => {
    let lastMonthRevenueOnsite = 0;
    let lastMonthRevenueExport = 0;
    let lastMonthSoldOnsite = 0;
    let lastMonthSoldExport = 0;
    let lastMonthGeneration = 0;
    const lastMonthIndex = getLatestActualsMonthIndex(site);
    if (lastMonthIndex !== undefined) {
      const lastMonthEnergyFlows =
        site.energyFinancialsMonthly[lastMonthIndex].energy.actual!;
      const lastMonthFinancialModels =
        site.energyFinancialsMonthly[lastMonthIndex].financial.actual!;
      const isOwnerOccupier = isSiteOwnerOccupied(site.siteOwnerships);
      const revenue = getFinancialPerformance(
        lastMonthFinancialModels,
        isOwnerOccupier,
        site.activeInvestmentModel
      )!;
      lastMonthRevenueOnsite =
        revenue.revenueOnsitePV +
        revenue.revenueOnsiteBattery +
        revenue.revenueOnsiteTariffArbitrage;
      lastMonthRevenueExport = revenue.revenueExport;
      lastMonthSoldOnsite = lastMonthEnergyFlows.behindMeter;
      lastMonthSoldExport = lastMonthEnergyFlows.exported;
      lastMonthGeneration = lastMonthEnergyFlows.generation;
    }

    return (
      <OperationalLandlordSiteRow
        key={site.id}
        site={site}
        organisation={organisation}
        lastMonthRevenueOnsite={lastMonthRevenueOnsite}
        lastMonthRevenueExport={lastMonthRevenueExport}
        lastMonthSoldOnsite={lastMonthSoldOnsite}
        lastMonthSoldExport={lastMonthSoldExport}
        lastMonthGeneration={lastMonthGeneration}
        showHealthIndicator={showHealthIndicator}
      />
    );
  });

  return (
    <>
      <NoPaddedRow style={{ marginTop: "8px" }}>
        <TableListSortFilters
          textFilter={textFilter}
          setTextFilter={setTextFilter}
        />
      </NoPaddedRow>
      <NoPaddedRow>
        <Table head={tableHead} rows={tableRows} />
      </NoPaddedRow>
    </>
  );
};

export default OperationalLandlordSiteListView;
